import { DocContent } from '../docsData';

const doc: DocContent = {
  title: 'Charts',
  content: `
The Analysis screen in Pliable empowers users to build dynamic reports and visualizations using pre-defined metrics and dimensions from the Semantic Layer. Designed for both technical and non-technical users, it simplifies data exploration, enables self-service insights, and integrates seamlessly with dashboards — all while maintaining consistency across dev/prod environments.

---

## Overview

The Analysis screen allows you to:

- **Visualize Data**: Choose from tables, bar charts, line charts, or premium analytics (e.g., LTV).
- **Drill Down**: Click any value to explore underlying data.
- **Export**: Download datasets as CSV for external use.
- **Save & Reuse**: Save analyses for later access or combine them into dashboards.

Analyses automatically leverage Pliable's Semantic Layer, ensuring accurate joins, aggregations, and filters without manual SQL or coding.

---

## Creating an Analysis

1. **Navigate to the Analysis Screen**: Navigate to the Analyses tab in the top navigation bar and click on **New Analysis**.

2. **Select Visualization Type**: Choose from the available chart types.

3. **Configure Metrics and Dimensions**:
   - **Metrics**: Select aggregated values (e.g., Total Revenue, Customer Count).
   - **Dimensions**: Group data by attributes (e.g., Region, Product Category).

   > 💡 Tip: Metrics and dimensions must be predefined in the Semantic Layer.

4. **Apply Filters**:
   - Use the **Date Range** dropdown to limit data to specific periods.
   - Add custom filters (e.g., Status = Active) under **Other Filters**.

5. **Run Analysis**: Click **Run** to generate the visualization.

---

## Key Features

### Drill-Down Functionality

Click any value in a table or chart to automatically drill into granular data. For example:

- Click a **Total Revenue** cell to view contributing orders.
- Click a **Region** bar to see metrics by sub-region.

### Exporting Data

1. Click the **Download CSV** button (top-right corner).
2. Select columns or apply final filters before exporting.

### Saving Analyses

1. Click **Save for Later**.
2. Name the analysis (e.g., "Monthly Sales Trends").
3. Access saved analyses via the **Saved Analyses** library.

### Dashboard Integration

Saved analyses can be added to dashboards:

1. In a dashboard, click **Add Component > Analysis**.
2. Select the saved analysis.
3. Filters (e.g., date ranges, product names) will auto-sync across components.

---

## Develop vs. Production Modes

- **Develop Mode**:
  - Build and test analyses with draft metrics/dimensions.
  - Publish changes to production when ready.

- **Production Mode**:
  - Non-technical stakeholders can explore data safely using published metrics.
  - All joins and aggregations are locked to ensure data integrity.

---

## Premium Analytics (Beta)

Premium features like **LTV Analysis** require additional configuration:

1. Define LTV metrics (e.g., Total Sales, CAC) in the Semantic Layer.
2. Select **Premium: LTV** as the visualization type.
3. Configure cumulative views, time periods, and customer cohorts.

> 📎  Premium analytics are marked as beta and may have limited drill-down capabilities.

---

## Tips & Best Practices

- **Leverage Dimensions**: Use descriptive attributes (e.g., Campaign Name) to slice data meaningfully.
- **Combine Metrics**: Create ratios (e.g., Revenue per Customer) using complex metrics.
- **Monitor Data Types**: Ensure numeric columns (e.g., INTEGER, DECIMAL) are mapped correctly for aggregations.

---

## FAQs

**Why can't I see certain columns in my analysis?**
Columns must be mapped to entities in the Semantic Layer. Learn more about [Data Mapping](/docs/data-mapping).

**How do I troubleshoot mismatched data types?**
Check column configurations in your entity's [Data Types](/docs/column-formatting) settings.
`,
};

export default doc;
